<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import type { Dictionary } from 'lodash';
  import { push } from 'svelte-spa-router';
  import { Button, NavigationDrawer, List, ListItem } from 'svelte-materialify';
  import { ProjectConfigService, ReportService } from '../../modules';
  import { alert, report, wrapProgress } from '../../store';
  import { getRemainingMinutesToPublish, minutesToHM } from '../../lib/date';

  export let activeCategory: string;

  enum Validation {
    VALID = 'valid',
    INVALID = 'invalid',
    WARNING = 'warning',
  }

  const validationEmojis = {
    [Validation.VALID]: '✅',
    [Validation.INVALID]: '🔴',
    [Validation.WARNING]: '⚠️',
  };
  const projectConfig = ProjectConfigService.get($report.project);
  let remainingTimeInterval = -1;
  let remainingMinutesToPublish = 0;
  let formattedRemainingTime = '';
  let reportControlStatus: boolean = false;

  $: validations = getValidations($report.itemCounts);
  $: isCategoriesReadyForPublish = Object.values(validations).every(
    (validation) => validation === Validation.VALID
  );

  onMount(() => {
    setRemainingTimes();
    remainingTimeInterval = window.setInterval(setRemainingTimes, 1000 * 60); // 1 minutes
  });

  onDestroy(() => {
    clearInterval(remainingTimeInterval);
  });

  function setRemainingTimes() {
    remainingMinutesToPublish = getRemainingMinutesToPublish(
      $report.date,
      projectConfig.publishTime
    );
    formattedRemainingTime = minutesToHM(remainingMinutesToPublish);
  }

  function setPreviewControl(previewControl: string) {
    window.localStorage.setItem('previewControl', previewControl);
  }

  function getPreviewControl() {
    return window.localStorage.getItem('previewControl');
  }

  function getValidations(itemCounts: Dictionary<number>): Dictionary<Validation> {
    return projectConfig.categories.reduce((validations, categoryConfig) => {
      const itemCount = itemCounts[categoryConfig.key];
      const minCountValid = itemCount >= categoryConfig.minCount;
      const sizeValid = categoryConfig.cols
        ? (itemCount - categoryConfig.minCount) % categoryConfig.cols === 0
        : true;

      validations[categoryConfig.key] = !minCountValid
        ? Validation.INVALID
        : !sizeValid
        ? Validation.WARNING
        : Validation.VALID;
      if (validations[categoryConfig.key] !== Validation.VALID) {
        setPreviewControl('Notconrolled');
      }
      previewControl();
      return validations;
    }, {});
  }

  function getCategoryProgress(itemCounts: Dictionary<number>, category: string) {
    return `${itemCounts?.[category] || 0} / ${getMaxCount(category)}`;
  }

  function navigateToCategory(category: string) {
    push(`/report/${$report.project}/${$report.date}/${category}`);
  }

  function getCategoryConfig(category: string) {
    return ProjectConfigService.getCategoryConfig($report.project, category);
  }

  function getMaxCount(category: string) {
    const { maxCount, minCount } = getCategoryConfig(category);

    if (maxCount === minCount) {
      return maxCount;
    }

    return `${minCount} - ${maxCount || '∞'}`;
  }

  async function publishReport() {
    const publishedReport = await ReportService.publish($report.project, $report.date);

    report.setActiveReport({
      ...$report,
      datePublished: publishedReport.datePublished,
    });

    alert.show('Report published successfully');
  }

  async function scheduleReport() {
    const scheduledReport = await ReportService.schedule($report.project, $report.date);

    report.setActiveReport({
      ...$report,
      dateScheduled: scheduledReport.dateScheduled,
    });

    alert.show('Report scheduled successfully');
  }

  async function unscheduleReport() {
    const unscheduledReport = await ReportService.unschedule(
      $report.project,
      $report.date
    );

    report.setActiveReport({
      ...$report,
      dateScheduled: unscheduledReport.dateScheduled,
    });

    alert.show('Report unscheduled successfully');
  }

  function previewReport() {
    ReportService.previewReport($report.project, $report.date);
    if (isCategoriesReadyForPublish) {
      setPreviewControl('Controlled');
      previewControl();
    }
  }

  function previewControl() {
    reportControlStatus = getPreviewControl() === 'Controlled';
  }
</script>

<NavigationDrawer fixed>
  <div on:click>
    <List>
      {#each projectConfig.categories as categoryConfig}
        <ListItem
          on:click={() => navigateToCategory(categoryConfig.key)}
          active={activeCategory === categoryConfig.key}
        >
          <span slot="prepend" class="mr-3">
            {validationEmojis[validations[categoryConfig.key]]}
          </span>
          {categoryConfig.name}
          <span slot="append"
            >({getCategoryProgress($report.itemCounts, categoryConfig.key)})</span
          >
        </ListItem>
      {/each}
    </List>
  </div>
  <span slot="append" class="pa-2">
    <Button
      class={isCategoriesReadyForPublish ? 'green white-text mb-2' : 'mb-2'}
      block
      on:click={previewReport}>Preview</Button
    >
    {#if !$report.datePublished && !$report.dateScheduled}
      {#if remainingMinutesToPublish > 0}
        <Button
          block
          on:click={wrapProgress(scheduleReport)}
          disabled={!reportControlStatus}>Schedule</Button
        >
      {/if}
      {#if remainingMinutesToPublish <= 0}
        <Button
          block
          on:click={wrapProgress(publishReport)}
          disabled={!reportControlStatus}>Publish</Button
        >
      {/if}
    {:else if $report.datePublished}
      <Button block disabled={true}>Published</Button>
    {:else}
      <Button block disabled={true}>Scheduled</Button>
      <Button block class="red white-text mt-2" on:click={wrapProgress(unscheduleReport)}
        >Unschedule</Button
      >
    {/if}
    <p
      class="text-center text-caption mb-0 mt-2"
      class:passed={remainingMinutesToPublish < 0}
    >
      {formattedRemainingTime}
    </p>
  </span>
</NavigationDrawer>
